import type { Locale } from "~/utils/locale";
import { t } from "~/utils/translations";

/**
 * Shared sort dropdown for product listing pages.
 *
 * Minimal wrapper around a native <select>. We stay accessibility-friendly
 * (screen readers announce "Sort by" label + options) and avoid custom
 * popovers so the dropdown works on every mobile browser.
 */

export interface SortOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  options: SortOption[];
  onChange: (value: string) => void;
  locale: Locale;
  className?: string;
}

export function SortDropdown({ value, options, onChange, locale, className = "" }: Props) {
  return (
    <label className={`flex items-center gap-2 text-[11px] text-black ${className}`}>
      <span className="uppercase tracking-[0.12em] text-[#737373] hidden sm:inline">
        {t("sortBy", locale)}:
      </span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none h-8 pl-0 pr-6 bg-transparent text-[11px] uppercase tracking-[0.12em] text-black focus:outline-none cursor-pointer"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <svg
          className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 text-black pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </label>
  );
}
